import {
  useCallback,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Modal,
  ValidationProvider,
  CheckBox,
  InfoBar,
  useDisclosure,
} from "@etframework/ui.components";
import useModalEvent from "../../hooks/useModalEvent";
import { useRequestBoardAccess } from "../../data/mutations/boardMutations";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SuccessSvg from "../../content/img/success.svg";
import { useQualifyingBoards } from "../../data/queries/boardPermissionRequestQueries";

export const RequestBoardAccessModal = ({ isAdmin }) => {
  const { t, ready } = useTranslation(["RequestAccessModal"], {
    useSuspense: false,
  });
  const boardListRef = useRef(null);
  const requestAccessModalRef = useRef(null);
  const [opened, { open, close }] = useDisclosure(false);
  const { useRegisterEvent, ModalEvents } = useModalEvent();
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showRequestSent, setShowRequestSent] = useState(false);

  const { mutate: requestBoardAccess, isPending } = useRequestBoardAccess();

  const buttons = [
    {
      buttonModel: {
        content: t("Cancel"),
        cssClass: "e-flat",
      },
      click: () => {
        close();
        setShowRequestSent(false);
      },
    },
    {
      buttonModel: {
        content: isPending ? t("Loading") : t("Confirm"),
        cssClass: isPending ? "e-disabled" : "e-primary",
        isPrimary: true,
        disabled: isPending,
      },
      click: () => {
        const selectedBoards = boardListRef.current.getSelectedBoards();
        if (selectedBoards.length === 0) {
          setError(t("EmptySelection"));
          return;
        } else {
          setError(null);

          requestBoardAccess(
            { boardIds: selectedBoards },
            {
              onSuccess: () => {
                if (isAdmin) {
                  navigate("/");
                } else {
                  setShowRequestSent(true);
                }
              },
              onError: (error) => {
                console.error("error", error);
                setError(t("RequestAccessError"));
              },
            }
          );
        }
      },
    },
  ];

  const BoardList = forwardRef((_, ref) => {
    const [selectedBoards, setSelectedBoards] = useState([]);
    const { data: boards, isPending, isSuccess } = useQualifyingBoards();
    const listRef = useRef(null);

    const allSelected = boards?.length === selectedBoards.length;

    const onChange = useCallback(({ checked }, board) => {
      if (checked) {
        setSelectedBoards((prev) => [...prev, board.Id]);
      } else {
        setSelectedBoards((prev) => prev.filter((id) => id !== board.Id));
      }
    }, []);

    const handleSelectAll = useCallback(({ checked }) => {
      if (checked) {
        setSelectedBoards(boards.map((board) => board.Id));
      } else {
        setSelectedBoards([]);
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getSelectedBoards: () => selectedBoards,
      }),
      [selectedBoards]
    );

    if (isPending && !isSuccess) {
      return <p>{t("Loading")}</p>;
    }

    return (
      <div>
        <CheckBox
          label={t("SelectAll")}
          change={handleSelectAll}
          checked={allSelected}
        />
        <ul
          ref={listRef}
          className="mt-3 space-y-6 list-unstyled ps-2"
          style={{ maxHeight: "300px", overflowY: "auto" }}
        >
          {boards.map((board) => (
            <li key={board.Id}>
              <CheckBox
                boldLabel={true}
                label={board.Name}
                description={board.Description}
                checked={allSelected || selectedBoards.includes(board.Id)}
                change={(e) => onChange(e, board)}
              />
            </li>
          ))}
        </ul>
      </div>
    );
  });

  const ModalContent = ({ error, boardListRef, showRequestSent }) => {
    if (showRequestSent) {
      return (
        <div className="d-flex flex-column align-items-center gap-3">
          <img src={SuccessSvg} alt="Success" className="w-40 h-40" />
          <div className="text-center">
            <h2 className="fs-6">{t("Success.Title")}</h2>
            <p>{t("Success.Message")}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <header>
          <h2 className="fs-5">{t("Title")}</h2>
          <span className="text-muted">{t("SelectInstruction")}</span>
        </header>
        <div>{error && <InfoBar variant="error" message={error} />}</div>
        <BoardList ref={boardListRef} />
      </div>
    );
  };

  useRegisterEvent(ModalEvents.RequestBoardAccessModal, open);

  if (!ready) return null;

  return (
    <ValidationProvider>
      <Modal
        id={ModalEvents.RequestBoardAccessModal.Id}
        ref={requestAccessModalRef}
        isModal
        useSpinner
        showCloseIcon={true}
        visible={opened}
        content={
          <ModalContent
            error={error}
            boardListRef={boardListRef}
            showRequestSent={showRequestSent}
          />
        }
        close={() => {
          close();
          setShowRequestSent(false);
        }}
        minHeight="20vh"
        width="500px"
        buttons={showRequestSent ? [] : buttons}
      />
    </ValidationProvider>
  );
};
